import { companyOverviewAccessMethods } from "./ncrmCompanyOverviewAccess.mjs";

const normalizePlacementId = (value) => {
  const normalized = Number(value || 0);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

const buildOverviewSections = ({ t, itemsByType, resolveOverviewRoute }) => {
  const withRoutes = (items) =>
    (Array.isArray(items) ? items : []).map((item) => ({ ...item, route: resolveOverviewRoute(item) }));
  return [
    {
      key: "contract",
      title: t("ncrm.contacts.details.overview.contractsTitle"),
      emptyLabel: t("ncrm.contacts.details.overview.emptyContracts"),
      items: withRoutes(itemsByType.contract),
    },
    {
      key: "onboarding",
      title: t("ncrm.contacts.details.overview.onboardingsTitle"),
      emptyLabel: t("ncrm.contacts.details.overview.emptyOnboardings"),
      items: withRoutes(itemsByType.onboarding),
    },
    {
      key: "deal",
      title: t("ncrm.contacts.details.overview.dealBoardsTitle"),
      emptyLabel: t("ncrm.contacts.details.overview.emptyDealBoards"),
      items: withRoutes(itemsByType.deal),
    },
    {
      key: "lead",
      title: t("ncrm.contacts.details.overview.leadBoardsTitle"),
      emptyLabel: t("ncrm.contacts.details.overview.emptyLeadBoards"),
      items: withRoutes(itemsByType.lead),
    },
  ];
};

export const companyOverviewComputed = {
  leadBoardsOverviewItems() {
    const source = Array.isArray(this.record?.lead_boards) ? this.record.lead_boards : [];
    return source.map((entry) => this.normalizeOverviewPlacement(entry, "lead")).filter(Boolean);
  },
  dealBoardsOverviewItems() {
    const source = Array.isArray(this.record?.deal_boards) ? this.record.deal_boards : [];
    return source.map((entry) => this.normalizeOverviewPlacement(entry, "deal")).filter(Boolean);
  },
  contractsOverviewItems() {
    const source = Array.isArray(this.record?.contracts) ? this.record.contracts : [];
    return source.map((entry) => this.normalizeOverviewPlacement(entry, "contract")).filter(Boolean);
  },
  onboardingsOverviewItems() {
    const source = Array.isArray(this.record?.onboardings) ? this.record.onboardings : [];
    return source.map((entry) => this.normalizeOverviewPlacement(entry, "onboarding")).filter(Boolean);
  },
  overviewSections() {
    return buildOverviewSections({
      t: this.$t,
      itemsByType: {
        lead: this.leadBoardsOverviewItems,
        deal: this.dealBoardsOverviewItems,
        contract: this.contractsOverviewItems,
        onboarding: this.onboardingsOverviewItems,
      },
      resolveOverviewRoute: (item) => this.resolveOverviewRoute(item),
    });
  },
  hasOverviewBoardItems() {
    return this.overviewSections.some((section) => Array.isArray(section?.items) && section.items.length > 0);
  },
  nonEmptyOverviewSections() {
    return this.overviewSections.filter(
      (section) => Array.isArray(section?.items) && section.items.length > 0
    );
  },
};

export const companyOverviewMethods = {
  normalizeOverviewPlacement(entry, sourceType) {
    const source = entry && typeof entry === "object" ? entry : null;
    if (!source) return null;
    const normalizedSourceType = String(sourceType || "").trim().toLowerCase();
    const placementKeyMap = {
      lead: "entry_id",
      deal: "deal_id",
      contract: "contract_id",
      onboarding: "onboarding_id",
    };
    const stageIdKey = normalizedSourceType === "contract" ? "deal_stage_id" : "stage_id";
    const stageNameKey = normalizedSourceType === "contract" ? "deal_stage_name" : "stage_name";
    return {
      sourceType: normalizedSourceType,
      placementId: normalizePlacementId(source?.[placementKeyMap[normalizedSourceType]]),
      boardId: normalizePlacementId(source?.board_id),
      boardName: String(source?.board_name || "").trim(),
      stageId: normalizePlacementId(source?.[stageIdKey]),
      stageName: String(source?.[stageNameKey] || "").trim(),
      status: String(source?.status || "").trim().toLowerCase(),
      dealId: normalizePlacementId(source?.deal_id),
      contractId: normalizePlacementId(source?.contract_id),
      contractNumber: String(source?.contract_number || "").trim(),
      contractName: String(source?.contract_name || "").trim(),
      serviceNameSnapshot: String(source?.service_name_snapshot || "").trim(),
    };
  },
  buildOverviewItemKey(item) {
    const sourceType = String(item?.sourceType || "").trim().toLowerCase() || "unknown";
    const primaryId = Number(item?.placementId || 0) || Number(item?.contractId || 0) || Number(item?.dealId || 0) || 0;
    const boardId = Number(item?.boardId || 0) || 0;
    const stageId = Number(item?.stageId || 0) || 0;
    return `company-overview-${sourceType}-${primaryId}-${boardId}-${stageId}`;
  },
  ...companyOverviewAccessMethods,
  formatOverviewItemTitle(item) {
    const sourceType = String(item?.sourceType || "").trim().toLowerCase();
    if (sourceType === "contract") return this.formatOverviewContractName(item);
    if (sourceType === "onboarding") return this.formatOverviewOnboardingBoardName(item);
    return this.formatOverviewBoardName(item);
  },
  formatOverviewItemSubtitle(item) {
    const sourceType = String(item?.sourceType || "").trim().toLowerCase();
    if (sourceType === "deal") return this.formatOverviewDealSubtitle(item);
    if (sourceType === "contract") return this.formatOverviewContractSubtitle(item);
    if (sourceType === "onboarding") return this.formatOverviewOnboardingSubtitle(item);
    return this.formatOverviewLeadSubtitle(item);
  },
  formatOverviewBoardName(item) {
    const boardName = String(item?.boardName || "").trim();
    if (boardName) return boardName;
    const fallbackId = Number(item?.boardId || 0);
    if (Number.isFinite(fallbackId) && fallbackId > 0) {
      return this.$t("ncrm.contacts.details.overview.boardFallback", { id: fallbackId });
    }
    return this.$t("ncrm.contacts.details.overview.boardFallback", { id: "?" });
  },
  formatOverviewOnboardingBoardName(item) {
    const boardName = String(item?.boardName || "").trim();
    if (boardName) return boardName;
    const fallbackId = Number(item?.boardId || 0);
    if (Number.isFinite(fallbackId) && fallbackId > 0) {
      return this.$t("ncrm.contacts.details.overview.onboardingBoardFallback", { id: fallbackId });
    }
    return this.$t("ncrm.contacts.details.overview.onboardingBoardFallback", { id: "?" });
  },
  formatOverviewStageName(item) {
    return String(item?.stageName || "").trim() || this.$t("ncrm.contacts.details.overview.stageFallback");
  },
  formatOverviewLeadSubtitle(item) {
    const parts = [];
    if (normalizePlacementId(item?.placementId)) {
      parts.push(this.$t("ncrm.contacts.details.overview.leadEntryLabel", { id: Number(item.placementId) }));
    }
    parts.push(this.$t("ncrm.contacts.details.overview.stageLabel", { stage: this.formatOverviewStageName(item) }));
    return parts.join(" • ");
  },
  formatOverviewDealSubtitle(item) {
    const parts = [];
    if (normalizePlacementId(item?.placementId)) {
      parts.push(this.$t("ncrm.contacts.details.overview.dealLabel", { id: Number(item.placementId) }));
    }
    parts.push(this.$t("ncrm.contacts.details.overview.stageLabel", { stage: this.formatOverviewStageName(item) }));
    return parts.join(" • ");
  },
  formatOverviewContractName(item) {
    const contractNumber = String(item?.contractNumber || "").trim();
    const descriptiveName = String(item?.contractName || "").trim() || String(item?.serviceNameSnapshot || "").trim();
    if (contractNumber && descriptiveName) return `${contractNumber} • ${descriptiveName}`;
    if (contractNumber) return contractNumber;
    if (descriptiveName) return descriptiveName;
    const placementId = normalizePlacementId(item?.placementId);
    return this.$t("ncrm.contacts.details.overview.contractLabel", { id: placementId || "?" });
  },
  formatOverviewContractReference(item) {
    const contractNumber = String(item?.contractNumber || "").trim();
    if (contractNumber) return contractNumber;
    const contractId = normalizePlacementId(item?.contractId || item?.placementId);
    return contractId ? this.$t("ncrm.contacts.details.overview.contractLabel", { id: contractId }) : "";
  },
  formatOverviewContractSubtitle(item) {
    const parts = [];
    const boardName = String(item?.boardName || "").trim();
    if (boardName) parts.push(boardName);
    if (normalizePlacementId(item?.dealId)) {
      parts.push(this.$t("ncrm.contacts.details.overview.dealLabel", { id: Number(item.dealId) }));
    }
    if (String(item?.stageName || "").trim()) {
      parts.push(this.$t("ncrm.contacts.details.overview.stageLabel", { stage: this.formatOverviewStageName(item) }));
    }
    return parts.join(" • ");
  },
  formatOverviewOnboardingSubtitle(item) {
    const parts = [];
    if (normalizePlacementId(item?.placementId)) {
      parts.push(this.$t("ncrm.contacts.details.overview.onboardingLabel", { id: Number(item.placementId) }));
    }
    const contractReference = this.formatOverviewContractReference(item);
    if (contractReference) parts.push(contractReference);
    parts.push(this.$t("ncrm.contacts.details.overview.stageLabel", { stage: this.formatOverviewStageName(item) }));
    return parts.join(" • ");
  },
  formatOverviewStatus(sourceType, status) {
    const normalizedSourceType = String(sourceType || "").trim().toLowerCase();
    const normalized = String(status || "").trim().toLowerCase() || "open";
    const statusGroupBySource = {
      lead: "ncrm.leads.statuses",
      deal: "ncrm.deals.statuses",
      contract: "ncrm.contracts.statuses",
      onboarding: "ncrm.onboarding.statuses",
    };
    const labelKey = `${statusGroupBySource[normalizedSourceType] || "ncrm.leads.statuses"}.${normalized}`;
    const translated = this.$t(labelKey);
    return translated === labelKey ? normalized : translated;
  },
  resolveOverviewStatusColor(sourceType, status) {
    const normalizedSourceType = String(sourceType || "").trim().toLowerCase();
    const normalizedStatus = String(status || "").trim().toLowerCase();
    if (normalizedSourceType === "lead") {
      if (normalizedStatus === "converted") return "success";
      if (normalizedStatus === "failed") return "error";
      if (normalizedStatus === "disqualified") return "warning";
      if (normalizedStatus === "no_response") return "neutral";
      return "primary";
    }
    if (normalizedSourceType === "contract") {
      if (normalizedStatus === "fulfilled" || normalizedStatus === "completed") return "success";
      if (normalizedStatus === "expired" || normalizedStatus === "terminated") return "warning";
      if (normalizedStatus === "canceled") return "error";
      return "primary";
    }
    if (normalizedSourceType === "onboarding") {
      if (normalizedStatus === "completed") return "success";
      if (normalizedStatus === "blocked") return "warning";
      if (normalizedStatus === "canceled") return "error";
      return "primary";
    }
    if (normalizedStatus === "won") return "success";
    if (normalizedStatus === "lost") return "error";
    return "primary";
  },
};
